'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useRef, useState, type FormEvent } from 'react';

import { AddressForm, type AddressFormHandle } from '@/components/checkout/AddressForm';
import { AddressSummary } from '@/components/checkout/AddressSummary';
import { computeBillingErrors } from '@/features/checkout/delivery/validation';
import { useCountryAreaFieldConfig } from '@/hooks/useCountryAreaFieldConfig';
import type { BillingAddressFormData, JpFormData } from '@/lib/checkout';
import type { ParsedAddress } from '@/lib/address/types';
import { EMPTY_JP_FORM, isJapanLocale } from '@/lib/checkout';
import { getTitleFieldLabel, getTitleOptions } from '@/lib/forms/titleOptions';
import { getCountryFromLocale } from '@/lib/i18n';
import type { ServiceAddress } from '@/lib/service/ecs/contracts';
import {
  mapServiceAddressToFormData,
  mapServiceAddressToSummary,
} from '@/lib/service/repairDetailsAddress';

import { RepairDetailsSection } from './RepairDetailsSection';

interface RepairDetailsAddressSectionProps {
  title: string;
  testId: string;
  address: ServiceAddress;
  canChange: boolean;
  isProcessing: boolean;
  onSubmit: (address: BillingAddressFormData) => Promise<void>;
}

export function RepairDetailsAddressSection({
  title,
  testId,
  address,
  canChange,
  isProcessing,
  onSubmit,
}: Readonly<RepairDetailsAddressSectionProps>) {
  const locale = useLocale();
  const tForms = useTranslations('forms');
  const tCheckout = useTranslations('checkout');
  const countryCode = getCountryFromLocale(locale).toUpperCase();
  const showCountryArea = countryCode !== 'HK';
  const showCountry = countryCode !== 'HK';
  const isJP = isJapanLocale(locale);
  const formRef = useRef<AddressFormHandle>(null);
  const [formData, setFormData] = useState<BillingAddressFormData>(() =>
    mapServiceAddressToFormData(address)
  );
  const [titleValue, setTitleValue] = useState(address.title ?? '');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [jpFormData, setJpFormData] = useState<JpFormData>(EMPTY_JP_FORM);
  const countryAreaConfig = useCountryAreaFieldConfig(formData.country || address.country);
  const titleOptions = getTitleOptions(countryCode, (key) => tForms(key));
  const titleFieldLabel = getTitleFieldLabel(countryCode, (key) => tForms(key));
  const summaryAddress = mapServiceAddressToSummary(address);

  const handleInputChange = (field: keyof BillingAddressFormData, value: string) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleAddressSelect = (parsed: ParsedAddress) => {
    setFormData((current) => ({
      ...current,
      streetAddress1: parsed.streetAddress1 ?? current.streetAddress1,
      streetAddress2: parsed.streetAddress2 ?? current.streetAddress2,
      city: parsed.city ?? current.city,
      postalCode: parsed.postalCode ?? current.postalCode,
      countryArea: parsed.countryArea ?? current.countryArea,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors = computeBillingErrors({
      billingData: formData,
      billingTitle: titleValue,
      tRequired: tCheckout('delivery.requiredField'),
      countryAreaRequired: showCountryArea && countryAreaConfig.isRequired,
    });
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (isJP && formRef.current) {
      const melissa = await formRef.current.validateWithMelissa();
      if (!melissa.isValid) return;
    }

    await onSubmit(formData);
  };

  return (
    <RepairDetailsSection
      title={title}
      testId={testId}
      canChange={canChange}
      isProcessing={isProcessing}
      summary={<AddressSummary address={summaryAddress} showChangeButton={false} />}
    >
      {({ onCancel }) => (
        <form
          id={`${testId}-form`}
          data-testid={`${testId}-form`}
          noValidate
          onSubmit={(event) => {
            void handleSubmit(event).catch(() => undefined);
          }}
          onReset={onCancel}
        >
          <AddressForm
            ref={formRef}
            embedded
            formData={formData}
            onInputChange={handleInputChange}
            formErrors={formErrors}
            onSetFormErrors={setFormErrors}
            variant="billing"
            isJP={isJP}
            jpFormData={jpFormData}
            onJpInputChange={(field, value) =>
              setJpFormData((current) => ({ ...current, [field]: value }))
            }
            titleOptions={titleOptions}
            titleFieldLabel={titleFieldLabel}
            title={titleValue}
            onTitleChange={setTitleValue}
            onAddressSelect={handleAddressSelect}
            disableCountry
            showCountryArea={showCountryArea}
            showCountry={showCountry}
            lang={locale}
          />
        </form>
      )}
    </RepairDetailsSection>
  );
}
